/**
 * SUB-5.9 · F-98. Which dashboard you were last looking at, and WHERE you were.
 *
 * ⚠️ THE BUG THIS CLOSES. `joules_last_viewed_dashboard_id` held a bare uuid and
 * nothing else. It was written by `DashboardView` and read by `Sidebar`, and it
 * was never cleared: not on tenant switch, not on scope change, not on sign out.
 * A dashboard id belongs to exactly one tenant (`dashboards.tenant_id` is NOT
 * NULL), so after switching accounts the Dashboards row navigated to a dashboard
 * belonging to the account just left, RLS refused the read, and the user got
 * `toast.error('Dashboard not found')` plus the error screen.
 *
 * ⚠️ THAT SYMPTOM READS AS A PERMISSIONS FAILURE AND IS NOT ONE. It is a stale
 * pointer. `DefaultDashboardRedirect` carries the same warning about USR-1.5,
 * which was a different cause with an identical presentation, and was
 * misdiagnosed the same way.
 *
 * ⚠️ THE FIX IS NOT TO DELETE THE MEMORY. The obvious shortcut is to drop the
 * read and always resolve the default. That fixes the bug and makes the feature
 * worse: it would forget your working dashboard every single time. The memory is
 * a good feature with a missing qualifier.
 *
 * WHAT QUALIFIES IT. Two things, both of which change which dashboards exist:
 *   - the tenant whose dashboards are listed. `dashboardService.getDashboards`
 *     filters on `get_current_user_tenant_id()`, so a memory from another
 *     account can never be valid here.
 *   - the scope mode. Entering or leaving All Accounts changes what the
 *     Dashboards surface means, and SUB-5.10 will add an entry that exists in
 *     one mode and not the other.
 *
 * ⚠️ IT IS A MAP, NOT A SINGLE RECORD, and that is deliberate. A single record
 * would satisfy the finding and would still forget your dashboard every time you
 * looked at another account and came back. Superadmins here switch between 11
 * tenants. Remembering per scope costs a few lines and is the behaviour people
 * actually want.
 *
 * These functions are pure and take the raw stored string, so the decision is
 * testable without a DOM or a `localStorage`. `src/lib/mergedFilters.ts` and
 * `src/lib/projectListFilters.ts` are the same pattern.
 */

import type { MergedScopeStatus } from './mergedScope';

/** Where the qualified memory lives. */
export const LAST_VIEWED_DASHBOARD_KEY = 'joules_last_viewed_dashboard';

/**
 * ⚠️ The unqualified key this story replaces. It is READ ONLY to be deleted.
 * Its value is a bare uuid with no tenant and no mode, so it cannot be validated
 * and must not be guessed at. Discarding it falls through to the existing
 * "default dashboard, else first dashboard" path, which is correct behaviour.
 */
export const LEGACY_LAST_VIEWED_DASHBOARD_KEY = 'joules_last_viewed_dashboard_id';

/**
 * How many scopes are remembered. Eleven tenants exist today and each can be
 * seen in two modes, so this holds every real scope with room to spare. The cap
 * exists so a long-lived browser cannot grow the entry unboundedly, not because
 * anyone is expected to reach it.
 */
export const MAX_REMEMBERED_SCOPES = 24;

interface StoredEntry {
  /** The scope key, from `dashboardScopeKey`. */
  k: string;
  /** The dashboard id last viewed in that scope. */
  id: string;
}

interface StoredShape {
  v: 1;
  /** Most recently used LAST, so eviction takes from the front. */
  entries: StoredEntry[];
}

/**
 * The scope a dashboard memory is valid in, or `null` when there is no answer
 * yet.
 *
 * ⚠️ AN UNRESOLVED SCOPE RETURNS NULL RATHER THAN A GUESS. Until the status
 * resolves we do not know which account's dashboards are listed, and following a
 * memory on a guess is the bug this file exists to close. A null key means read
 * nothing and write nothing, so the caller falls through to resolving the
 * default, which is always safe.
 */
export function dashboardScopeKey(
  status: MergedScopeStatus,
  currentTenantId: string | null | undefined,
): string | null {
  if (!status.resolved) return null;
  if (!currentTenantId) return null;
  return `${status.scopeMode}:${currentTenantId}`;
}

function parse(raw: string | null | undefined): StoredShape {
  const empty: StoredShape = { v: 1, entries: [] };
  if (!raw) return empty;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return empty;
    const shape = parsed as Record<string, unknown>;
    if (shape.v !== 1 || !Array.isArray(shape.entries)) return empty;
    const entries = shape.entries.filter(
      (entry): entry is StoredEntry =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as StoredEntry).k === 'string' &&
        typeof (entry as StoredEntry).id === 'string' &&
        (entry as StoredEntry).k.length > 0 &&
        (entry as StoredEntry).id.length > 0,
    );
    return { v: 1, entries };
  } catch {
    // ⚠️ Corrupt storage is an empty memory, never a thrown error. This runs on
    // the path that renders the sidebar.
    return empty;
  }
}

/**
 * The dashboard last viewed in this scope, or `null`.
 *
 * Returns null for a scope that was never visited, which is the same answer as
 * "never viewed one", and the caller treats both the same way.
 */
export function readLastViewedDashboard(
  raw: string | null | undefined,
  scopeKey: string | null,
): string | null {
  if (!scopeKey) return null;
  const found = parse(raw).entries.find((entry) => entry.k === scopeKey);
  return found ? found.id : null;
}

/**
 * The memory with `dashboardId` recorded against `scopeKey`, serialised.
 *
 * Returns the raw string unchanged when there is no scope to record against, so
 * an unresolved scope cannot overwrite a good memory with an unqualified one.
 */
export function writeLastViewedDashboard(
  raw: string | null | undefined,
  scopeKey: string | null,
  dashboardId: string | null | undefined,
): string | null {
  if (!scopeKey || !dashboardId) return raw ?? null;

  const current = parse(raw);
  const withoutScope = current.entries.filter((entry) => entry.k !== scopeKey);
  const next = [...withoutScope, { k: scopeKey, id: dashboardId }];

  // Oldest first, so the slice keeps the most recently used.
  const capped = next.slice(Math.max(0, next.length - MAX_REMEMBERED_SCOPES));

  return JSON.stringify({ v: 1, entries: capped } satisfies StoredShape);
}

/**
 * Forget one scope, used when a remembered dashboard turns out not to load. A
 * pointer that failed once will fail every time until something clears it, and
 * leaving it in place means the user meets the same error on every visit.
 */
export function forgetLastViewedDashboard(
  raw: string | null | undefined,
  scopeKey: string | null,
): string | null {
  if (!scopeKey) return raw ?? null;
  const current = parse(raw);
  if (!current.entries.some((entry) => entry.k === scopeKey)) return raw ?? null;
  return JSON.stringify({
    v: 1,
    entries: current.entries.filter((entry) => entry.k !== scopeKey),
  } satisfies StoredShape);
}
